import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { PortalConfigService } from '../../_services/portal-config.service';

/**
 * Standalone safety/feature toggles for a single portal tenant's config:
 * payment_sandbox (already existed as a config field, but was only ever
 * editable via the raw JSON textarea - this gives it a proper on/off
 * switch), communications_disabled (a blanket kill switch for all outbound
 * SMS/email on this tenant, broader than the existing billing-only
 * billing_dry_run flag), billing_enabled (skips this tenant entirely in
 * the daily billing cron - no invoices, no charges),
 * billing_charge_attempts_enabled (narrower than billing_enabled: invoices
 * still generate on schedule, but no automatic gateway charge is
 * submitted - for a tenant mid-migration off a legacy billing system that
 * is still the one actually charging the customer this cycle), and
 * workflows_enabled (skips the subscription status state machine for this
 * tenant - added after a Gaiia-imported customer got a real activation
 * workflow re-triggered by an import; off means status changes are
 * recorded but nothing downstream, e.g. install notifications, fires).
 * Same
 * shared/reused pattern as share-portal-billing-settings - lives in one
 * place, used by both the System Management "Portal Configuration Editor"
 * and Portal Management's own Settings tab.
 *
 * Emits saved/saveError rather than showing its own toast, since the two
 * host pages use different notification services.
 */
@Component({
  selector: 'share-portal-safety-flags',
  templateUrl: './share-portal-safety-flags.component.html'
})
export class SharePortalSafetyFlagsComponent implements OnChanges {
  @Input() portalTenantId: number | null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() saveError = new EventEmitter<any>();

  loading = false;
  saving = false;

  paymentSandbox = true;
  communicationsDisabled = false;
  billingEnabled = true;
  billingChargeAttemptsEnabled = true;
  workflowsEnabled = true;

  constructor(private pcs: PortalConfigService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['portalTenantId'] && this.portalTenantId) {
      this.loadConfig();
    }
  }

  loadConfig(): void {
    if (!this.portalTenantId) return;

    this.loading = true;
    this.pcs.getConfig(this.portalTenantId).subscribe({
      next: (response: any) => {
        const config = response.body?.config || {};
        this.paymentSandbox = config.payment_sandbox !== false;
        this.communicationsDisabled = config.communications_disabled === true;
        this.billingEnabled = config.billing_enabled !== false;
        this.billingChargeAttemptsEnabled = config.billing_charge_attempts_enabled !== false;
        this.workflowsEnabled = config.workflows_enabled !== false;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  save(): void {
    if (!this.portalTenantId || this.saving) return;

    this.saving = true;
    this.pcs
      .updateConfig(this.portalTenantId, {
        config: {
          payment_sandbox: this.paymentSandbox,
          communications_disabled: this.communicationsDisabled,
          billing_enabled: this.billingEnabled,
          billing_charge_attempts_enabled: this.billingChargeAttemptsEnabled,
          workflows_enabled: this.workflowsEnabled
        }
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.saved.emit();
        },
        error: (error) => {
          this.saving = false;
          this.saveError.emit(error);
        }
      });
  }
}
